'use client';

import {
  type ColumnFiltersState,
  type Row,
  type RowSelectionState,
  type Table as TableInstance,
  type Updater,
} from '@tanstack/react-table';
import { createElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExpandedSubItemCheckbox } from './ExpandedSubItemCheckbox';
import type {
  DataGridExpandedRowContentOptions,
  DataGridExpandedSubItem,
  DataGridRowSelectionChangeSource,
  DataGridRowSelectionState,
} from './types';

function countTotalSelected(rows: RowSelectionState, expandedSubItems: Record<string, Record<string, boolean>>): number {
  return (
    Object.values(rows).filter(Boolean).length +
    Object.values(expandedSubItems).reduce((sum, subs) => sum + Object.values(subs).filter(Boolean).length, 0)
  );
}

interface UseDataGridSelectionArgs<TData = unknown> {
  enableRowSelection: boolean;
  initialRowSelection: DataGridRowSelectionState | undefined;
  getSelectableSubItems: ((row: Row<TData>) => DataGridExpandedSubItem[]) | undefined;
  syncParentRowSelection: boolean;
  preserveRowSelectionOnFilter: boolean;
  onRowSelectionChange: ((selection: DataGridRowSelectionState, options: { source: DataGridRowSelectionChangeSource }) => void) | undefined;
  search: string;
  columnFilters: ColumnFiltersState;
  isLoading: boolean;
  resolveSelectedCountLabel: (count: number) => string;
  resolveSelectionOpenedLabel: (count: number) => string;
  internalTableRef: React.RefObject<HTMLTableElement | null>;
  /** Ref to the table instance, owned by DataGrid and set after useReactTable. Used to call table.getRow() for sub-item sync. */
  tableInstanceRef: React.RefObject<TableInstance<TData> | null>;
}

export interface UseDataGridSelectionReturn<TData = unknown> {
  rowSelection: RowSelectionState;
  expandedSubItemSelection: Record<string, Record<string, boolean>>;
  expandedSubItemSelectionRef: React.MutableRefObject<Record<string, Record<string, boolean>>>;
  selectionAnnouncement: string;
  /** Stable ref tracking what triggered the current selection change. Set before calling TanStack toggles. */
  selectionSourceRef: React.MutableRefObject<DataGridRowSelectionChangeSource>;
  handleClearSelection: () => void;
  handleExpandedSubItemToggle: (row: Row<TData>, subItemId: string, selected: boolean) => void;
  /** Pass to useReactTable's onRowSelectionChange. */
  handleRowSelectionChange: (updater: Updater<RowSelectionState>) => void;
  createExpandedRowContentOptions: (row: Row<TData>) => DataGridExpandedRowContentOptions | undefined;
  hasExpandedSubItemSelection: boolean;
  hasSelection: boolean;
}

/**
 * Manages all row and sub-item selection state for DataGrid.
 *
 * Handles:
 * - Row and expanded sub-item selection state
 * - Parent ↔ sub-item sync (`syncParentRowSelection`)
 * - Auto-clear on search/filter change
 * - Screen reader announcements on selection change
 * - `createExpandedRowContentOptions` factory for expanded row checkboxes
 *
 * ### Table ref wiring
 * `handleRowSelectionChange` needs `table.getRow()` to sync sub-item selection when a row is
 * toggled via TanStack (e.g. select-all). Because the table instance is created after this hook,
 * DataGrid owns the `tableInstanceRef` and sets it after `useReactTable`:
 * ```ts
 * const tableInstanceRef = useRef<TableInstance<TData> | null>(null);
 * const selection = useDataGridSelection({ tableInstanceRef, ... });
 * const table = useReactTable({ onRowSelectionChange: selection.handleRowSelectionChange, ... });
 * tableInstanceRef.current = table;
 * ```
 */
export function useDataGridSelection<TData = unknown>({
  enableRowSelection,
  initialRowSelection,
  getSelectableSubItems,
  syncParentRowSelection,
  preserveRowSelectionOnFilter,
  onRowSelectionChange,
  search,
  columnFilters,
  isLoading,
  resolveSelectedCountLabel,
  resolveSelectionOpenedLabel,
  internalTableRef,
  tableInstanceRef,
}: UseDataGridSelectionArgs<TData>): UseDataGridSelectionReturn<TData> {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(initialRowSelection?.rows ?? {});
  const [expandedSubItemSelection, setExpandedSubItemSelection] = useState<Record<string, Record<string, boolean>>>(
    initialRowSelection?.expandedSubItems ?? {},
  );
  const [selectionAnnouncement, setSelectionAnnouncement] = useState('');
  const selectionAnnouncementMountedRef = useRef(false);
  const prevTotalCountRef = useRef(countTotalSelected(initialRowSelection?.rows ?? {}, initialRowSelection?.expandedSubItems ?? {}));

  const selectionSourceRef = useRef<DataGridRowSelectionChangeSource>('row');
  const rowSelectionRef = useRef(rowSelection);
  rowSelectionRef.current = rowSelection;
  const expandedSubItemSelectionRef = useRef(expandedSubItemSelection);
  expandedSubItemSelectionRef.current = expandedSubItemSelection;

  const handleClearSelection = useCallback(() => {
    setRowSelection({});
    setExpandedSubItemSelection({});
    onRowSelectionChange?.({ rows: {}, expandedSubItems: {} }, { source: 'clear' });
    // rAF ensures the DOM has updated (selection bar unmounted) before moving focus
    requestAnimationFrame(() => {
      internalTableRef.current?.focus();
    });
  }, [onRowSelectionChange, internalTableRef]);

  const handleExpandedSubItemToggle = useCallback(
    (row: Row<TData>, subItemId: string, selected: boolean) => {
      const rowId = row.id;
      // Read from refs to avoid stale closure on rapid successive toggles
      const currentExpandedSubItemSelection = expandedSubItemSelectionRef.current;
      const currentRowSelection = rowSelectionRef.current;

      const rowSubItems = { ...(currentExpandedSubItemSelection[rowId] ?? {}) };
      if (selected) {
        rowSubItems[subItemId] = true;
      } else {
        delete rowSubItems[subItemId];
      }

      const newExpandedSubItemSelection = { ...currentExpandedSubItemSelection };
      if (Object.keys(rowSubItems).length > 0) {
        newExpandedSubItemSelection[rowId] = rowSubItems;
      } else {
        delete newExpandedSubItemSelection[rowId];
      }

      setExpandedSubItemSelection(newExpandedSubItemSelection);

      // Sync parent row selection: select parent when all sub-items selected, deselect otherwise
      let newRowSelection = currentRowSelection;
      if (syncParentRowSelection && getSelectableSubItems) {
        const allSubItems = getSelectableSubItems(row);
        const selectedCount = Object.values(newExpandedSubItemSelection[rowId] ?? {}).filter(Boolean).length;
        const allSelected = allSubItems.length > 0 && selectedCount === allSubItems.length;

        newRowSelection = { ...currentRowSelection };
        if (allSelected) {
          newRowSelection[rowId] = true;
        } else {
          delete newRowSelection[rowId];
        }
        setRowSelection(newRowSelection);
      }

      onRowSelectionChange?.({ rows: newRowSelection, expandedSubItems: newExpandedSubItemSelection }, { source: 'expanded-sub-item' });
    },
    [onRowSelectionChange, getSelectableSubItems, syncParentRowSelection],
  );

  const handleRowSelectionChange = useCallback(
    (updater: Updater<RowSelectionState>) => {
      // Read from refs to avoid stale closure — TanStack may call this handler once per render
      // cycle, but using refs ensures we always operate on the latest state without needing
      // rowSelection/expandedSubItemSelection in the deps array (which would force recreation
      // of this handler on every selection change).
      const currentRowSelection = rowSelectionRef.current;
      const currentExpandedSubItemSelection = expandedSubItemSelectionRef.current;
      const newRowSelection = typeof updater === 'function' ? updater(currentRowSelection) : updater;
      let newExpandedSubItemSelection = currentExpandedSubItemSelection;

      if (getSelectableSubItems && tableInstanceRef.current) {
        const changedRowIds = [...new Set([...Object.keys(newRowSelection), ...Object.keys(currentRowSelection)])].filter(
          (id) => newRowSelection[id] !== currentRowSelection[id],
        );

        if (changedRowIds.length > 0) {
          newExpandedSubItemSelection = { ...currentExpandedSubItemSelection };
          for (const rowId of changedRowIds) {
            const row = tableInstanceRef.current.getRowModel().rowsById[rowId];
            if (!row) continue;
            const subItems = getSelectableSubItems(row);
            if (subItems.length === 0) continue;
            if (newRowSelection[rowId]) {
              newExpandedSubItemSelection[rowId] = Object.fromEntries(subItems.map((sr) => [sr.id, true]));
            } else {
              delete newExpandedSubItemSelection[rowId];
            }
          }
        }
      }

      setRowSelection(newRowSelection);
      setExpandedSubItemSelection(newExpandedSubItemSelection);
      onRowSelectionChange?.(
        { rows: newRowSelection, expandedSubItems: newExpandedSubItemSelection },
        { source: selectionSourceRef.current },
      );
    },
    [onRowSelectionChange, getSelectableSubItems, tableInstanceRef],
  );

  // Clear selection on search/filter change.
  // Intentionally does NOT clear when returning to an empty filter state (search cleared, all filters removed)
  // because removing filters expands the visible data set — previously selected rows remain visible,
  // so keeping the selection is valid and avoids surprising the user.
  // Skipped entirely when `preserveRowSelectionOnFilter` is true (e.g. "pick from saved" modals
  // that pre-seed `initialRowSelection` and need checkbox visuals to survive filtering).
  useEffect(() => {
    if (!enableRowSelection) return;
    if (preserveRowSelectionOnFilter) return;
    if (!search && columnFilters.length === 0) return;

    // Skip if nothing is selected — avoids spurious onRowSelectionChange calls on every keystroke
    const hasRows = Object.keys(rowSelectionRef.current).length > 0;
    const hasSubItems = Object.keys(expandedSubItemSelectionRef.current).length > 0;
    if (!hasRows && !hasSubItems) return;

    setRowSelection({});
    setExpandedSubItemSelection({});
    onRowSelectionChange?.({ rows: {}, expandedSubItems: {} }, { source: 'filter' });
  }, [search, columnFilters, enableRowSelection, preserveRowSelectionOnFilter, onRowSelectionChange]);

  // Announce selection count to screen readers on every toggle.
  // Skips the initial mount so "0 selected" isn't announced before the user has interacted.
  // Always announces the current count (including 0) so both select-all and deselect-all are voiced.
  // Deselect ("0 selected") followed by re-select ("N selected") always produces different text,
  // so aria-live detects a content change and announces reliably.
  useEffect(() => {
    if (!enableRowSelection) return;
    if (!selectionAnnouncementMountedRef.current) {
      selectionAnnouncementMountedRef.current = true;
      return;
    }
    const count = countTotalSelected(rowSelection, expandedSubItemSelection);
    const prevCount = prevTotalCountRef.current;
    prevTotalCountRef.current = count;
    setSelectionAnnouncement(prevCount === 0 && count > 0 ? resolveSelectionOpenedLabel(count) : resolveSelectedCountLabel(count));
  }, [rowSelection, expandedSubItemSelection, enableRowSelection, resolveSelectedCountLabel, resolveSelectionOpenedLabel]);

  const createExpandedRowContentOptions = useCallback(
    (row: Row<TData>): DataGridExpandedRowContentOptions | undefined => {
      if (!enableRowSelection) return undefined;
      const subItems = getSelectableSubItems?.(row) ?? [];
      return {
        renderSubItemCheckbox: (subItem, overrides) => {
          if (process.env.NODE_ENV !== 'production' && !getSelectableSubItems) {
            console.warn(
              '[DataGrid] renderSubItemCheckbox was called but getSelectableSubItems was not provided. ' +
                'Sub-item selection state, indeterminate indicators, and parent row syncing will not work. ' +
                'Pass getSelectableSubItems to DataGrid alongside getExpandedRowContent.',
            );
          }
          return createElement(ExpandedSubItemCheckbox, {
            key: subItem.id,
            subItem,
            selected: expandedSubItemSelectionRef.current[row.id]?.[subItem.id] ?? false,
            onToggle: (subItemId: string, selected: boolean) => handleExpandedSubItemToggle(row, subItemId, selected),
            disabled: overrides?.disabled ?? isLoading,
            showLabel: overrides?.showLabel,
          });
        },
        subItems,
        subItemSelection: expandedSubItemSelectionRef.current[row.id] ?? {},
        selectAllSubItems: () => {
          const newSubItemSelection = Object.fromEntries(subItems.map((sr) => [sr.id, true]));
          const prevExpandedSubItemSelection = expandedSubItemSelectionRef.current;
          const prevRowSelection = rowSelectionRef.current;
          const newExpandedSubItemSelection = { ...prevExpandedSubItemSelection, [row.id]: newSubItemSelection };
          const newRowSelection = syncParentRowSelection ? { ...prevRowSelection, [row.id]: true } : prevRowSelection;
          setExpandedSubItemSelection((prev) => ({ ...prev, [row.id]: newSubItemSelection }));
          if (syncParentRowSelection) setRowSelection((prev) => ({ ...prev, [row.id]: true }));
          onRowSelectionChange?.({ rows: newRowSelection, expandedSubItems: newExpandedSubItemSelection }, { source: 'expanded-sub-item' });
        },
        deselectAllSubItems: () => {
          const prevExpandedSubItemSelection = expandedSubItemSelectionRef.current;
          const prevRowSelection = rowSelectionRef.current;
          const newExpandedSubItemSelection = { ...prevExpandedSubItemSelection };
          delete newExpandedSubItemSelection[row.id];
          const newRowSelection = syncParentRowSelection ? { ...prevRowSelection } : prevRowSelection;
          if (syncParentRowSelection) delete newRowSelection[row.id];
          setExpandedSubItemSelection((prev) => {
            const next = { ...prev };
            delete next[row.id];
            return next;
          });
          if (syncParentRowSelection) {
            setRowSelection((prev) => {
              const next = { ...prev };
              delete next[row.id];
              return next;
            });
          }
          onRowSelectionChange?.({ rows: newRowSelection, expandedSubItems: newExpandedSubItemSelection }, { source: 'expanded-sub-item' });
        },
      };
    },
    [enableRowSelection, getSelectableSubItems, isLoading, syncParentRowSelection, onRowSelectionChange, handleExpandedSubItemToggle],
  );

  const hasExpandedSubItemSelection = useMemo(
    () => Object.values(expandedSubItemSelection).some((subs) => Object.values(subs).some(Boolean)),
    [expandedSubItemSelection],
  );

  const hasRowSelection = useMemo(() => Object.values(rowSelection).some(Boolean), [rowSelection]);
  const hasSelection = hasRowSelection || hasExpandedSubItemSelection;

  return {
    rowSelection,
    expandedSubItemSelection,
    expandedSubItemSelectionRef,
    selectionAnnouncement,
    selectionSourceRef,
    handleClearSelection,
    handleExpandedSubItemToggle,
    handleRowSelectionChange,
    createExpandedRowContentOptions,
    hasExpandedSubItemSelection,
    hasSelection,
  };
}
